import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Search, Filter, RefreshCw, Plus, AlertTriangle } from 'lucide-react';
import {
  assignmentsFilterAtom,
  assignmentsPriorityFilterAtom,
  assignmentsSearchQueryAtom,
  assignmentsLoadingAtom,
  assignmentsErrorAtom,
  fetchAssignmentsActionAtom,
} from '@/stores/assignmentsStore';

export interface AssignmentHeaderProps {
  allAssignmentsCount: number;
  displayAssignmentsCount: number;
  activeTab: string;
  onActiveTabChange: (tab: string) => void;
  onNewAssignment: () => void;
}

export function AssignmentHeader({
  allAssignmentsCount,
  displayAssignmentsCount,
  activeTab,
  onActiveTabChange,
  onNewAssignment,
}: AssignmentHeaderProps) {
  const [statusFilter, setStatusFilter] = useAtom(assignmentsFilterAtom);
  const [priorityFilter, setPriorityFilter] = useAtom(
    assignmentsPriorityFilterAtom
  );
  const [searchQuery, setSearchQuery] = useAtom(assignmentsSearchQueryAtom);
  const isLoading = useAtomValue(assignmentsLoadingAtom);
  const error = useAtomValue(assignmentsErrorAtom);
  const fetchAssignments = useSetAtom(fetchAssignmentsActionAtom);

  const handleRefresh = () => {
    fetchAssignments().catch(console.error);
  };
  return (
    <div className='p-4 border-b border-border bg-background shrink-0 text-left'>
      <div className='flex items-center justify-between mb-4'>
        <div>
          <h2 className='text-xl font-medium'>Assignments</h2>
          <p className='text-sm text-muted-foreground'>
            Manage journalism assignments and track progress through the
            AI-powered workflow
            {allAssignmentsCount > 0 && (
              <span className='ml-2'>
                ({allAssignmentsCount} total, {displayAssignmentsCount} shown)
              </span>
            )}
          </p>
        </div>
        <div className='flex items-center gap-2'>
          <Button
            variant='outline'
            size='sm'
            onClick={handleRefresh}
            disabled={isLoading}
            className='gap-1'
          >
            <RefreshCw
              className={`h-3 w-3 ${isLoading ? 'animate-spin' : ''}`}
            />
          </Button>
          <Button size='sm' className='gap-2' onClick={onNewAssignment}>
            <Plus className='h-4 w-4' />
            New Assignment
          </Button>
        </div>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={onActiveTabChange}
        className='mb-4'
      >
        <TabsList>
          <TabsTrigger value='all'>All Assignments</TabsTrigger>
          <TabsTrigger value='active'>Active</TabsTrigger>
          <TabsTrigger value='drafts'>Drafts</TabsTrigger>
          <TabsTrigger value='published'>Published</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className='flex gap-4 mb-4'>
        <div className='relative flex-1'>
          <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground' />
          <Input
            placeholder='Search assignments...'
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className='pl-10'
          />
        </div>
        <Select
          value={statusFilter}
          onValueChange={value => setStatusFilter(value as any)}
        >
          <SelectTrigger className='w-48'>
            <Filter className='h-4 w-4 mr-2' />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='all'>All Status</SelectItem>
            <SelectItem value='created'>Created</SelectItem>
            <SelectItem value='briefing_prepared'>Briefing Prepared</SelectItem>
            <SelectItem value='interview_scheduled'>
              Interview Scheduled
            </SelectItem>
            <SelectItem value='interview_in_progress'>
              Interview In Progress
            </SelectItem>
            <SelectItem value='interview_completed'>
              Interview Completed
            </SelectItem>
            <SelectItem value='transcript_ready'>Transcript Ready</SelectItem>
            <SelectItem value='draft_generated'>Draft Generated</SelectItem>
            <SelectItem value='under_review'>Under Review</SelectItem>
            <SelectItem value='approved'>Approved</SelectItem>
            <SelectItem value='published'>Published</SelectItem>
            <SelectItem value='cancelled'>Cancelled</SelectItem>
            <SelectItem value='overdue'>Overdue</SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={priorityFilter}
          onValueChange={value => setPriorityFilter(value as any)}
        >
          <SelectTrigger className='w-32'>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='all'>All Priority</SelectItem>
            <SelectItem value='urgent'>Urgent</SelectItem>
            <SelectItem value='high'>High</SelectItem>
            <SelectItem value='medium'>Medium</SelectItem>
            <SelectItem value='low'>Low</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {error && (
        <Alert variant='destructive' className='mb-4'>
          <AlertTriangle className='h-4 w-4' />
          <AlertDescription>
            {error}
            <Button
              variant='outline'
              size='sm'
              onClick={handleRefresh}
              className='ml-2'
            >
              Try Again
            </Button>
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}
